import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Skeleton from '@mlb/react-patterns/components/Skeleton/skeleton.tsx';

import { useImageUtils } from '../../hooks';
import Card from '../AspectRatioCard';

export const BASE_WIDTH = 344;
export const BASE_HEIGHT = 140;
const ASPECT_RATIO = `${BASE_WIDTH}:${BASE_HEIGHT}`;
const PLAYER_BASE_WIDTH = 116;
const LOGO_BASE_WIDTH = 196;
const LOGO_BASE_MARGIN = 16;
const LOGO_PERCENTAGE = (LOGO_BASE_WIDTH * 100) / BASE_WIDTH;
const PLAYER_PERCENTAGE = (PLAYER_BASE_WIDTH * 100) / BASE_WIDTH;

const Wrapper = styled.div`
    position: relative;
    display: block;
`;

const BackgroundImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const TeamLogo = styled.img`
    position: absolute;
    width: ${LOGO_BASE_WIDTH * 100 / BASE_WIDTH}%;
    top: 50%;
    left: ${LOGO_BASE_MARGIN * 100 / BASE_WIDTH}%;
    transform: translateY(-50%);
`;

const PlayerImage = styled.img`
    position: absolute;
    right: 0;
    bottom: 0;
    width: ${PLAYER_BASE_WIDTH * 100 / BASE_WIDTH}%;
    border-bottom-right-radius: 8px;
    filter: drop-shadow(-2px 0px 4px rgba(0, 0, 0, 0.25));
`;

const Overlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    ${({ overlayStyles }) => overlayStyles}
`;

const calcSizes = (cardWidths) => {

    const backgroundImgSizes = [];
    const teamLogoImgSizes = [];
    const playerImgSizes = [];

    cardWidths.forEach((cardWidth) => {

        const {
            mediaQuery = '', width,
        } = cardWidth;

        backgroundImgSizes.push(`${mediaQuery} calc(${width})`);
        teamLogoImgSizes.push(`${mediaQuery} calc(${width} * (${LOGO_PERCENTAGE} / 100))`);
        playerImgSizes.push(`${mediaQuery} calc(${width} * (${PLAYER_PERCENTAGE} / 100))`);

    });

    return {
        backgroundImgSizes: backgroundImgSizes.join(', '),
        teamLogoImgSizes: teamLogoImgSizes.join(', '),
        playerImgSizes: playerImgSizes.join(', '),
    };

};

const FavoriteTeamCard = (props) => {

    const {
        backgroundImageTemplateUrl,
        overlayStyles,
        teamLogoImageTemplateUrl,
        playerImageTemplateUrl,
        backgroundImageWidths,
        teamLogoImageWidths,
        playerImageWidths,
        loading,
        cardWidths,
        ...otherProps
    } = props;

    const { formatImageUrl } = useImageUtils();

    const backgroundImgSrc = formatImageUrl({
        templateUrl: backgroundImageTemplateUrl,
        width: backgroundImageWidths[backgroundImageWidths.length - 1],
        aspectRatio: ASPECT_RATIO,
    });

    const teamLogoImgSrc = formatImageUrl({
        templateName: 'transparencyFit',
        templateUrl: teamLogoImageTemplateUrl,
        width: teamLogoImageWidths[teamLogoImageWidths.length - 1],
    });

    const playerImgSrc = formatImageUrl({
        templateName: 'transparencyFit',
        templateUrl: playerImageTemplateUrl,
        width: playerImageWidths[playerImageWidths.length - 1],
    });

    const backgroundImgSrcSet = backgroundImageWidths.map((width) => `${formatImageUrl({
        templateUrl: backgroundImageTemplateUrl,
        width,
        aspectRatio: ASPECT_RATIO,
    })} ${width}w`).join(', ');

    const teamLogoImgSrcSet = teamLogoImageWidths.map((width) => `${formatImageUrl({
        templateName: 'transparencyFit',
        templateUrl: teamLogoImageTemplateUrl,
        width,
    })} ${width}w`).join(', ');

    const playerImgSrcSet = playerImageWidths.map((width) => `${formatImageUrl({
        templateName: 'transparencyFit',
        templateUrl: playerImageTemplateUrl,
        width,
    })} ${width}w`).join(', ');

    const {
        backgroundImgSizes, teamLogoImgSizes, playerImgSizes,
    } = calcSizes(cardWidths);

    return (
        <Wrapper {...otherProps}>
            <Card aspectRatio={ASPECT_RATIO}>
                {loading ? (
                    <Skeleton margin={0} />
                ): (
                    <>
                        <BackgroundImage
                            src={backgroundImgSrc}
                            srcSet={backgroundImgSrcSet}
                            sizes={backgroundImgSizes}
                            loading={'lazy'}
                        />
                        {overlayStyles && <Overlay overlayStyles={overlayStyles} />}
                        <TeamLogo
                            src={teamLogoImgSrc}
                            srcSet={teamLogoImgSrcSet}
                            sizes={teamLogoImgSizes}
                            loading={'lazy'}
                        />
                    </>
                )}
            </Card>
            {!loading && (
                <PlayerImage
                    src={playerImgSrc}
                    srcSet={playerImgSrcSet}
                    sizes={playerImgSizes}
                    loading={'lazy'}
                />
            )}
        </Wrapper>
    );

};

FavoriteTeamCard.propTypes = {
    backgroundImageTemplateUrl: PropTypes.string,
    overlayStyles: PropTypes.string,
    teamLogoImageTemplateUrl: PropTypes.string,
    playerImageTemplateUrl: PropTypes.string,
    backgroundImageWidths: PropTypes.array,
    teamLogoImageWidths: PropTypes.array,
    playerImageWidths: PropTypes.array,
    loading: PropTypes.bool,
    cardWidths: PropTypes.arrayOf(
        PropTypes.shape({
            mediaQuery: PropTypes.string,
            width: PropTypes.string,
        })
    ),
};

FavoriteTeamCard.defaultProps = {
    loading: false,
    backgroundImageWidths: [
        372,
        640,
        1024,
    ],
    teamLogoImageWidths: [
        168,
        372,
        640,
        1024,
    ],
    playerImageWidths: [
        120,
        168,
        372,
        640,
    ],
    cardWidths: [],
};

export default FavoriteTeamCard;
